import { CommercialFilterProvider } from "./CommercialFilterContext";
import { useState, useEffect } from "react";
import {
  Container,
  Tabs,
  Tab,
  Box,
  Typography,
  Stack,
  IconButton,
  Badge,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RestoreIcon from "@mui/icons-material/Restore";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useCoreBaseStore } from "./hooks/useCoreBaseStore";
import { useSubscriptionMetrics } from "./subscriptions/hooks";
import useOnboarding from "./hooks/useOnboarding";
import OnboardingOverlay from "./OnboardingOverlay";
import LeadsPipeline from "./LeadsPipeline";
import LeadsList from "./LeadsList";
import TareasFollowup from "./TareasFollowup";
import Ventas from "./Ventas";
import DashboardCRM from "./DashboardCRM";
import ExpansionPanel from "./expansion/ExpansionPanel";
import AlertasInteligentes from "./AlertasInteligentes";
import ConfigCoreBase from "./ConfigCoreBase";
import ConfigProductos from "./ConfigProductos";
import CreditosPanel from "./CreditosPanel";
import FeedbackWA from "../FeedbackWA";
import corebaseTheme from "./theme/corebaseTheme";
import useBrandScope from "./theme/useBrandScope";
import { BRAND } from "./theme/brand";

const TABS = [
  { key: "pipeline", label: "Pipeline", hint: "Tablero de prospectos por etapa", icon: <ViewKanbanOutlinedIcon />, onboardingId: "onb-pipeline-tab" },
  { key: "leads", label: "Leads", hint: "Lista, búsqueda e importación de leads", icon: <PeopleAltOutlinedIcon /> },
  { key: "seguimiento", label: "Seguimiento", hint: "Tareas y próximos contactos", icon: <EventNoteOutlinedIcon />, onboardingId: "onb-seguimiento-tab" },
  { key: "ventas", label: "Ventas", hint: "Registro e historial de ventas", icon: <PointOfSaleOutlinedIcon />, onboardingId: "onb-ventas-tab" },
  { key: "creditos", label: "Créditos", hint: "Cobranzas de ventas al crédito", icon: <AccountBalanceWalletOutlinedIcon />, onboardingId: "onb-creditos-tab" },
  { key: "expansion", label: "Oportunidades", hint: "Primera venta y ventas adicionales", icon: <TrendingUpOutlinedIcon /> },
  { key: "reportes", label: "Reportes", hint: "Indicadores, gráficas y descargas", icon: <InsightsOutlinedIcon />, onboardingId: "onb-dashboard-tab" },
  { key: "productos", label: "Productos", hint: "Catálogo de productos y servicios", icon: <Inventory2OutlinedIcon />, onboardingId: "onb-productos-tab" },
];

function tiempoRelativo(fecha) {
  if (!fecha) return "nunca";
  const diff = Math.floor((Date.now() - new Date(fecha).getTime()) / 60000);
  if (diff < 1) return "hace un momento";
  if (diff < 60) return `hace ${diff} min`;
  if (diff < 1440) return `hace ${Math.floor(diff / 60)} h`;
  return `hace ${Math.floor(diff / 1440)} d`;
}

function SyncIndicator({ store }) {
  const { syncStatus, ultimoSync, cambiosPendientes, forzarSync, pullSeguro, restaurarDesdeBackup } = store;
  const [snack, setSnack] = useState(null);

  const handlePull = async () => {
    const result = await pullSeguro();
    setSnack({ severity: result.ok ? "success" : "error", message: result.razon });
  };

  const handleRestore = () => {
    const ok = restaurarDesdeBackup();
    setSnack({
      severity: ok ? "info" : "warning",
      message: ok ? "Datos restaurados desde la copia local" : "No hay copia local disponible",
    });
  };

  const iconSx = { fontSize: 18 };
  const estado = store.connectionStatus === "offline" ? { label: "Sin conexión", color: "#FFC56B", icon: <CloudOffIcon sx={iconSx} /> } :
    syncStatus === "syncing"
      ? {
          label: "Guardando…",
          color: BRAND.light,
          icon: <CloudSyncIcon sx={{ ...iconSx, animation: "spin 1s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />,
        }
      : syncStatus === "error"
        ? { label: "Error al guardar", color: "#FF8A8A", icon: <CloudOffIcon sx={iconSx} /> }
        : cambiosPendientes
          ? { label: "Cambios sin subir", color: "#FFC56B", icon: <CloudUploadIcon sx={iconSx} /> }
          : { label: store.connectionStatus === "connected" ? "Actualizado" : store.connectionStatus === "polling" ? "Sync automático" : "Reconectando…", color: "#7BD8A8", icon: <CloudDoneIcon sx={iconSx} /> };

  const tooltip =
    syncStatus === "error"
      ? `No se pudo guardar. Último guardado: ${tiempoRelativo(ultimoSync)}. Clic para reintentar.`
      : `Último guardado: ${tiempoRelativo(ultimoSync)}. Clic para guardar ahora.`;

  const iconButtonSx = { color: "rgba(255,255,255,0.85)", "&:hover": { color: BRAND.white, bgcolor: "rgba(255,255,255,0.1)" } };

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={tooltip}>
          <Chip
            icon={estado.icon}
            label={estado.label}
            size="small"
            variant="outlined"
            onClick={forzarSync}
            data-onboarding-id="onb-sync-chip"
            sx={{
              height: 28,
              color: BRAND.white,
              borderColor: estado.color,
              bgcolor: "rgba(255,255,255,0.06)",
              "& .MuiChip-icon": { color: estado.color },
              "&:hover": { bgcolor: "rgba(255,255,255,0.14) !important" },
            }}
          />
        </Tooltip>
        <Tooltip title="Descargar datos desde la nube">
          <IconButton size="small" onClick={handlePull} aria-label="Descargar datos desde la nube" sx={iconButtonSx}>
            <CloudDownloadIcon sx={iconSx} />
          </IconButton>
        </Tooltip>
        {store.isOwner && <Tooltip title="Restaurar copia local">
          <IconButton size="small" onClick={handleRestore} aria-label="Restaurar copia local" sx={iconButtonSx}>
            <RestoreIcon sx={iconSx} />
          </IconButton>
        </Tooltip>}
      </Stack>
      {store.syncConflict && <Alert severity="warning" sx={{ my: 1 }}>
        {store.syncConflict.message || "Otro usuario cambió el mismo dato. Tus cambios pendientes se conservan."} El envío está pausado.
        <Stack direction="row" spacing={1}>
          <Button onClick={store.exportPendingChanges}>Descargar mis cambios</Button>
          <Button onClick={store.useServerVersion}>Usar versión del servidor</Button>
        </Stack>
      </Alert>}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled" sx={{ width: "100%" }}>
            {snack.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}

const ONBOARDING_STEPS = [
  // ── Fase 1: Core ──
  {
    id: "config",
    targetId: "onb-config-btn",
    title: "Configura tu negocio",
    description:
      "Primero lo primero: ingresa el nombre de tu negocio, moneda, zonas geográficas y datos generales. Haz clic en el ícono de engranaje para comenzar.",
  },
  {
    id: "pipeline",
    targetId: "onb-pipeline-tab",
    title: "Tu pipeline de ventas",
    description:
      "Aquí verás todos tus prospectos organizados en un tablero por etapas. Arrastra las tarjetas entre columnas para actualizar el estado de cada lead.",
    tab: "pipeline",
  },
  {
    id: "dashboard",
    targetId: "onb-dashboard-tab",
    title: "Explora tus reportes",
    description:
      "Visualiza tu embudo de ventas, conversiones y el rendimiento de tu equipo. Desde aquí también descargas reportes comparativos.",
    tab: "reportes",
  },
  // ── Fase 2: Leads & Productos ──
  {
    id: "add-lead",
    targetId: "onb-add-lead-btn",
    title: "Agrega tu primer lead",
    description:
      "Un lead es un cliente potencial interesado en tu producto. Haz clic en \"Nuevo lead\" para registrar su nombre, teléfono, canal de contacto y ubicación.",
    tab: "pipeline",
  },
  {
    id: "lead-card",
    targetId: "onb-lead-card",
    title: "¿Qué es un lead?",
    description:
      "Cada tarjeta representa un prospecto. Nuevo → Contactado → Interesado → Propuesta enviada → Venta. Arrastra las tarjetas y personaliza las columnas en Configuración.",
    tab: "pipeline",
  },
  {
    id: "config-productos",
    targetId: "onb-productos-tab",
    title: "Registra tus productos",
    description:
      "Configura tu catálogo de productos o servicios con nombre y precio. Si vendes suscripciones, activa la recurrencia con vigencia y días de recordatorio.",
    tab: "productos",
  },
  // ── Fase 3: WhatsApp ──
  {
    id: "config-plantillas",
    targetId: "onb-config-plantillas",
    title: "Plantillas de WhatsApp",
    description:
      "Crea mensajes prediseñados con variables como {nombre}, {curso}, {precio}. Asígnalas a etapas específicas para enviar el mensaje correcto en el momento justo.",
    openConfig: true,
  },
  {
    id: "send-wa",
    targetId: "onb-wa-btn",
    title: "Envía un WhatsApp",
    description:
      "Desde la lista de leads, haz clic en el ícono de WhatsApp para enviar una plantilla con los datos del prospecto. Se abre directamente en WhatsApp.",
    tab: "leads",
  },
  // ── Fase 4: Seguimiento & Ventas ──
  {
    id: "seguimiento",
    targetId: "onb-seguimiento-tab",
    title: "Seguimiento de leads",
    description:
      "Programa tareas de seguimiento: llamadas, visitas, mensajes. Ve las tareas pendientes, vencidas y completadas. Nunca pierdas de vista a un prospecto.",
  },
  {
    id: "ventas",
    targetId: "onb-ventas-tab",
    title: "Registra ventas",
    description:
      "Registra ventas con múltiples productos, efectivo y/o transferencia. Puedes marcar ventas al crédito con fecha límite de pago y anticipo.",
  },
  {
    id: "creditos",
    targetId: "onb-creditos-tab",
    title: "Créditos y cobranzas",
    description:
      "Gestiona las ventas al crédito: ve quién debe, cuánto pagó, registra abonos y recibe alertas cuando una deuda está por vencer o ya venció.",
  },
  // ── Fase 5: Analytics ──
  {
    id: "funnel",
    targetId: "onb-funnel",
    title: "Embudo de conversión",
    description:
      "Este gráfico muestra cuántos leads hay en cada etapa y dónde se pierden más. Identifica cuellos de botella para mejorar tu tasa de conversión.",
    tab: "reportes",
  },
  {
    id: "mapa",
    targetId: "onb-mapa-clientes",
    title: "Mapa de clientes",
    description:
      "Visualiza dónde se concentran tus clientes. Al registrar un lead con ubicación GPS, aparecerá en el mapa con colores por zona.",
    tab: "reportes",
  },
  {
    id: "rendimiento",
    targetId: "onb-rendimiento",
    title: "Rendimiento por canal",
    description:
      "Compara qué canal de ventas (WhatsApp, presencial, redes) genera más conversiones e ingresos. Invierte en los canales que mejor funcionan.",
    tab: "reportes",
  },
  // ── Fase 6: Extras ──
  {
    id: "alertas",
    targetId: "onb-alertas-btn",
    title: "Alertas inteligentes",
    description:
      "Recibe avisos de leads sin contacto, cobros vencidos y sugerencias de acción. La campana te mantiene al tanto de todo.",
  },
  {
    id: "cloud-sync",
    targetId: "onb-sync-chip",
    title: "Guardado en la nube",
    description:
      "Tus datos se guardan automáticamente en la nube. El indicador muestra el estado; haz clic para guardar ahora, descargar desde la nube o restaurar la copia local.",
  },
];

function CoreBaseContent({ workspace }) {
  useBrandScope();
  const [tab, setTab] = useState("pipeline");
  const [ventaLeadId, setVentaLeadId] = useState(null);
  const [ventaOpportunityId, setVentaOpportunityId] = useState(null);
  const nuevaVenta = (id, opportunityId = null) => { setVentaLeadId(id); setVentaOpportunityId(opportunityId); setTab("ventas"); };
  const [openConfig, setOpenConfig] = useState(false);
  const [openAlertas, setOpenAlertas] = useState(false);
  const rawStore = useCoreBaseStore(workspace);
  const [clientePorArchivar, setClientePorArchivar] = useState(null);
  const store = { ...rawStore, archivarLead: (id, archivado = true) => {
    if (!rawStore.isOwner) return;
    if (!archivado) { rawStore.archivarLead(id, false); return; }
    setClientePorArchivar(rawStore.leads.find(l => l.id === id) || null);
  } };
  const subs = useSubscriptionMetrics(store);
  const onboarding = useOnboarding(ONBOARDING_STEPS);
  const leidasSet = new Set(store.config.alertasLeidas || []);
  const totalAlertas = [...store.alertas, ...(subs.alertasSuscripcion || [])]
    .filter((a) => a.id && !leidasSet.has(a.id)).length;

  useEffect(() => {
    if (!store.isOwner || !onboarding.active || !onboarding.currentStep) return;
    const step = onboarding.currentStep;
    if (step.tab) setTab(step.tab);
    setOpenConfig(Boolean(step.openConfig));
  }, [store.isOwner, onboarding.active, onboarding.currentStep]);

  const headerIconSx = { color: "rgba(255,255,255,0.9)", "&:hover": { color: BRAND.white, bgcolor: "rgba(255,255,255,0.1)" } };

  if (store.accessError) return <Alert severity="error">{store.accessError}</Alert>;
  if (!store.ready) return <Box p={3}>Cargando espacio CRM...</Box>;

  return (
    <ThemeProvider theme={corebaseTheme}>
      <Container maxWidth={false} disableGutters sx={{ pb: 4, color: "text.primary", fontFamily: corebaseTheme.typography.fontFamily }}>
        {/* Cabecera de marca */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: "navy.main",
            color: BRAND.white,
            borderRadius: 3,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.75, sm: 2 },
            mb: 1.5,
            backgroundImage: `linear-gradient(120deg, ${BRAND.navy} 0%, ${BRAND.navy} 60%, #133760 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1.5, sm: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: BRAND.light, display: "block", lineHeight: 1.4 }}>
                CoreBase CRM
              </Typography>
              <Typography
                variant="h5"
                component="h1"
                sx={{ color: BRAND.white, fontSize: { xs: "1.25rem", sm: "1.5rem" }, overflowWrap: "anywhere" }}
              >
                {store.config.nombreNegocio}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
              <SyncIndicator store={store} />
              <Tooltip title={totalAlertas ? `${totalAlertas} alertas por revisar` : "Alertas"}>
                <IconButton onClick={() => setOpenAlertas(true)} data-onboarding-id="onb-alertas-btn" aria-label="Alertas" sx={headerIconSx}>
                  <Badge badgeContent={totalAlertas} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              {store.isOwner && <Tooltip title="Configuración del negocio">
                <IconButton onClick={() => setOpenConfig(true)} data-onboarding-id="onb-config-btn" aria-label="Configuración del negocio" sx={headerIconSx}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>}
            </Stack>
          </Stack>
        </Paper>

        {/* Navegación */}
        <Paper variant="outlined" sx={{ mb: 2.5, borderRadius: 3, px: { xs: 0.5, sm: 1 } }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            {TABS.filter(t => store.isOwner || t.key !== 'productos').map(t => (
              <Tab
                key={t.key}
                value={t.key}
                label={t.label}
                icon={t.icon}
                iconPosition="start"
                title={t.hint}
                sx={{ minHeight: 52, gap: 0.25, "& .MuiTab-iconWrapper": { fontSize: 20 } }}
                {...(t.onboardingId ? { "data-onboarding-id": t.onboardingId } : {})}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Contenido */}
        <Box>
          {tab === "pipeline" && <LeadsPipeline store={store} onNuevaVenta={nuevaVenta} />}
          {tab === "leads" && <LeadsList store={store} onNuevaVenta={nuevaVenta} />}
          {tab === "seguimiento" && <TareasFollowup store={store} />}
          {tab === "ventas" && <Ventas store={store} initialLeadId={ventaLeadId} initialOpportunityId={ventaOpportunityId} onInitialLeadConsumed={() => { setVentaLeadId(null); setVentaOpportunityId(null); }} />}
          {tab === "creditos" && <CreditosPanel store={store} />}
          {tab === "expansion" && <ExpansionPanel store={store} onNuevaVenta={nuevaVenta} />}
          {tab === "reportes" && <DashboardCRM store={{ ...store, leads: store.metricLeads, ventas: store.metricVentas, oportunidadesExpansion: store.metricOportunidades, leadsPorEtapa: Object.fromEntries(Object.entries(store.leadsPorEtapa).map(([k, rows]) => [k, rows.filter(l => store.isOwner || l.vendedorId === store.sellerId)])) }} onOpenConfig={() => store.isOwner && setOpenConfig(true)} onGoCreditos={() => setTab("creditos")} />}
          {tab === "productos" && (
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Productos</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Catálogo con nombre, precio y, si aplica, vigencia para suscripciones.
              </Typography>
              <ConfigProductos store={store} />
            </Paper>
          )}
        </Box>

        {/* Modales */}
        <Dialog open={Boolean(clientePorArchivar) && rawStore.isOwner} onClose={() => setClientePorArchivar(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Archivar cliente</DialogTitle>
          <DialogContent>
            <Typography>Al archivar a {clientePorArchivar?.nombre}, dejará de aparecer en todas las listas y reportes, junto con sus ventas, pagos, tareas y oportunidades. Solo aparecerá en Archivados.</Typography>
            <Typography sx={{ mt: 2 }}>Sus datos e historial se conservarán intactos. Puedes restaurarlo después para que vuelva a aparecer. El archivo y la restauración quedarán registrados en su historial de cambios.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClientePorArchivar(null)}>Cancelar</Button>
            <Button variant="contained" onClick={() => { rawStore.archivarLead(clientePorArchivar.id); setClientePorArchivar(null); }}>Archivar</Button>
          </DialogActions>
        </Dialog>
        {store.isOwner && <ConfigCoreBase open={openConfig} onClose={() => setOpenConfig(false)} store={store} />}
        <AlertasInteligentes open={openAlertas} onClose={() => setOpenAlertas(false)} store={store} subs={subs} />
        <FeedbackWA appName="CoreBase CRM" />

        {store.isOwner && onboarding.active && onboarding.currentStep && (
          <OnboardingOverlay
            currentStep={onboarding.currentStep}
            stepNumber={onboarding.stepNumber}
            totalSteps={onboarding.totalSteps}
            onNext={onboarding.next}
            onPrev={onboarding.prev}
            onDismiss={onboarding.dismiss}
          />
        )}
      </Container>
    </ThemeProvider>
  );
}

export default function CoreBaseCore(props) { return <CommercialFilterProvider key={props.workspace?.id || "local"}><CoreBaseContent {...props} /></CommercialFilterProvider>; }
